import React, { useContext } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";

import GlobalContext from "../../context/GlobalContext";

const SectionStyled = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle, var(--nd-border) 0.5px, transparent 0.5px);
    background-size: 12px 12px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const HeroBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  display: block;
  margin-bottom: 24px;
`;

const HeroTitle = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 64px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.03em;
  line-height: 1.0;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const HeroSubtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 500px;
  margin-bottom: 48px;
`;

const PrimaryButton = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  min-height: 44px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  transition: opacity 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  outline: none;

  &:hover { opacity: 0.85; }
`;

const SecondaryButton = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  min-height: 44px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  outline: none;

  &:hover { border-color: var(--nd-text-primary); }
`;

const MetricRow = styled.div`
  display: flex;
  gap: 48px;
  margin-top: 64px;
  flex-wrap: wrap;
  @media (max-width: 768px) { gap: 24px; }
`;

const MetricNumber = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 36px;
  font-weight: 700;
  color: var(--nd-text-display);
  line-height: 1;
  letter-spacing: -0.02em;
`;

const MetricLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-top: 4px;
`;

const ClientStrip = styled.div`
  margin-top: 64px;
  padding-top: 32px;
  border-top: 1px solid var(--nd-border);
`;

const ClientLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 16px;
`;

const ClientName = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-secondary);
  margin-right: 48px;

  &:last-child { margin-right: 0; }
`;

const Hero = () => {
  const gContext = useContext(GlobalContext);

  return (
    <SectionStyled>
      <Container>
        <HeroBadge>DEVOPS AS A SERVICE</HeroBadge>
        <HeroTitle>
          Ship Faster.<br />
          Scale Smarter.<br />
          Sleep Better.
        </HeroTitle>
        <HeroSubtitle>
          Small DevOps team that's been setting up cloud infrastructure
          for startups for 15 years. We're probably too responsive on Slack.
        </HeroSubtitle>

        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <PrimaryButton aria-label="Book a free consultation" onClick={(e) => { e.preventDefault(); gContext.toggleContact(); }}>
            BOOK A CONSULTATION
          </PrimaryButton>
          <SecondaryButton aria-label="See how we work" onClick={() => { const el = document.getElementById("services"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}>
            SEE HOW WE WORK
          </SecondaryButton>
          <SecondaryButton as="a" href="/Dioscuro_Services_Brochure.pdf" download aria-label="Download brochure" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            DOWNLOAD BROCHURE
          </SecondaryButton>
        </div>

        <MetricRow>
          <div><MetricNumber>50+</MetricNumber><MetricLabel>DEPLOYMENTS</MetricLabel></div>
          <div><MetricNumber>99.9%</MetricNumber><MetricLabel>UPTIME</MetricLabel></div>
          <div><MetricNumber>$500K+</MetricNumber><MetricLabel>CLOUD COSTS SAVED</MetricLabel></div>
          <div><MetricNumber>15+</MetricNumber><MetricLabel>YEARS EXP</MetricLabel></div>
        </MetricRow>

        <ClientStrip>
          <ClientLabel>TRUSTED BY</ClientLabel>
          <div><ClientName>Endava</ClientName><ClientName>PwC</ClientName></div>
        </ClientStrip>
      </Container>
    </SectionStyled>
  );
};

export default Hero;
